import {
  apiRequest,
  apiRequestWithETag,
  apiPatch,
  apiDelete,
  safeApiCall,
  buildUrl,
  mapApiResponse,
  ApiResponse,
} from './client';
import { AlbumVisibility, Album } from '../photoService';

// User interfaces
export interface UserProfile {
  id: string;
  displayName: string;
  email?: string;
  createdAt?: string;
  friendshipStatus?: FriendshipStatus;
  defaultAlbumVisibility?: AlbumVisibility;
}

export type FriendshipStatus = 'None' | 'Friends';

/**
 * Gets the current user's profile (triggers auto-creation on first login)
 */
export const getCurrentUser = (): Promise<UserProfile> =>
  safeApiCall(
    () => apiRequest<UserProfile>(buildUrl('/api/users/me')),
    "Error fetching current user"
  );

/**
 * ETag-aware variant of `getCurrentUser`.
 */
export const getCurrentUserWithETag = (etag?: string | null): Promise<ApiResponse<UserProfile>> =>
  apiRequestWithETag<UserProfile>(buildUrl('/api/users/me'), etag);

/**
 * Updates the current user's profile (display name and/or preferences)
 */
export const updateUserProfile = (updates: { 
  displayName?: string; 
  defaultAlbumVisibility?: AlbumVisibility;
}): Promise<UserProfile> =>
  safeApiCall(
    () => apiPatch<UserProfile>(buildUrl('/api/users/me'), updates),
    "Error updating user profile"
  );

/**
 * Gets a user's public profile by ID
 */
export const getUserById = (userId: string): Promise<UserProfile> =>
  safeApiCall(
    () => apiRequest<UserProfile>(buildUrl(`/api/users/${encodeURIComponent(userId)}`)),
    "Error fetching user"
  );

/**
 * ETag-aware variant of `getUserById`.
 */
export const getUserByIdWithETag = (
  userId: string,
  etag?: string | null
): Promise<ApiResponse<UserProfile>> =>
  apiRequestWithETag<UserProfile>(
    buildUrl(`/api/users/${encodeURIComponent(userId)}`),
    etag
  );

/**
 * Gets a user's public albums
 */
export const getUserAlbums = (
  userId: string,
  orderBy?: string,
  orderDirection?: string
): Promise<Album[]> =>
  safeApiCall(async () => {
    const albums = await apiRequest<Album[]>(buildUrl(`/api/users/${encodeURIComponent(userId)}/albums`, {
      orderBy,
      orderDirection,
    }));
    // Transform date strings from JSON into Date objects
    return albums.map((album) => ({
      ...album,
      creationDate: new Date(album.creationDate),
      oldestPhotoDate: album.oldestPhotoDate ? new Date(album.oldestPhotoDate) : undefined,
      newestPhotoDate: album.newestPhotoDate ? new Date(album.newestPhotoDate) : undefined,
    }));
  }, "Error fetching user albums");

/**
 * ETag-aware variant of `getUserAlbums`.
 */
export const getUserAlbumsWithETag = async (
  userId: string,
  orderBy?: string,
  orderDirection?: string,
  etag?: string | null
): Promise<ApiResponse<Album[]>> => {
  const response = await apiRequestWithETag<Album[]>(
    buildUrl(`/api/users/${encodeURIComponent(userId)}/albums`, { orderBy, orderDirection }),
    etag
  );
  return mapApiResponse(response, (albums) => albums.map((album) => ({
    ...album,
    creationDate: new Date(album.creationDate),
    oldestPhotoDate: album.oldestPhotoDate ? new Date(album.oldestPhotoDate) : undefined,
    newestPhotoDate: album.newestPhotoDate ? new Date(album.newestPhotoDate) : undefined,
  })));
};

/**
 * Deletes all user data (photos and albums). Keeps friendships, comments, and reactions.
 */
export const deleteUserDataViaApi = (): Promise<void> =>
  safeApiCall(
    () => apiDelete(buildUrl('/api/users/me/data')),
    "Error deleting user data"
  );

/**
 * Deletes the user account and all associated data.
 */
export const deleteUserViaApi = (): Promise<void> =>
  safeApiCall(
    () => apiDelete(buildUrl('/api/users/me')),
    "Error deleting user account"
  );
